import { toTitleCase } from "$routes/patient/_lib/text";
import type { AncEncounter, AncInvestigationOutcome } from "elephant-web/gql";
import { defineMessage, type MessageDescriptor } from "react-intl";
import {
  computeGestationalAge,
  computeGestationalAgeFromEdd,
  isEddManuallyOverridden,
} from "./gestational-age";

export enum AntenatalFormSections {
  generalInfo = "generalInfo",
  parity = "parity",
  dates = "dates",
  ageOfPregnancy = "ageOfPregnancy",
  investigations = "investigations",
  counselling = "counselling",
}

export enum AntenatalCareTabs {
  general = "general",
  investigations = "investigations",
  counselling = "counselling",
  prescriptions = "prescriptions",
}

export enum AntenatalFormFields {
  visitDate = "visitDate",
  deliveryDate = "expectedDeliveryDate",
  deliveries = "deliveries",
  miscarriages = "miscarriages",
  lastMenstrualPeriod = "lastMenstrualPeriod",
  gestationalAge = "gestationalAge",
  visitNumber = "visitNumber",
  maritalStatus = "maritalStatus",
  referralHospital = "referralHospital",
  bloodGroup = "bloodGroup",
  rhesusFactor = "rhesusFactor",
  gravidity = "gravidity",
  aliveBirths = "aliveBirths",
  livingChildren = "livingChildren",
  priorPregnancies = "priorPregnancies",
  vitals = "vitals",
  syphillis = "syphillis",
  hepatitisB = "hepatitisB",
  hepatitisC = "hepatitisC",
  haematology = "haematology",
  bloodSugar = "bloodSugar",
  urinalysis = "urinalysis",
  associatedProblems = "associatedProblems",
  hiv = "hiv",
  maternalNutrition = "maternalNutrition",
  breastfeeding = "breastfeeding",
  exclusiveBreastfeeding = "exclusiveBreastfeeding",
  femaleGenitalMutilation = "femaleGenitalMutilation",
  iptpDoseOverride = "iptpDoseOverride",
  longLastingInsecticidalNet = "longLastingInsecticidalNet",
}

export const fields = {
  gestationalAge: `${AntenatalFormSections.ageOfPregnancy}.${AntenatalFormFields.gestationalAge}`,
  lastMenstrualPeriod: `${AntenatalFormSections.ageOfPregnancy}.${AntenatalFormFields.lastMenstrualPeriod}`,
  deliveryDate: `${AntenatalFormSections.ageOfPregnancy}.${AntenatalFormFields.deliveryDate}`,
  visitDate: `${AntenatalFormSections.dates}.${AntenatalFormFields.visitDate}`,
  visitNumber: `${AntenatalFormSections.dates}.${AntenatalFormFields.visitNumber}`,
  miscarriages: `${AntenatalFormSections.parity}.${AntenatalFormFields.miscarriages}`,
  deliveries: `${AntenatalFormSections.parity}.${AntenatalFormFields.deliveries}`,
  maritalStatus: `${AntenatalFormSections.parity}.${AntenatalFormFields.maritalStatus}`,
  referralHospital: `${AntenatalFormSections.parity}.${AntenatalFormFields.referralHospital}`,
  bloodGroup: `${AntenatalFormSections.parity}.${AntenatalFormFields.bloodGroup}`,
  rhesusFactor: `${AntenatalFormSections.parity}.${AntenatalFormFields.rhesusFactor}`,
  gravidity: `${AntenatalFormSections.parity}.${AntenatalFormFields.gravidity}`,
  aliveBirths: `${AntenatalFormSections.parity}.${AntenatalFormFields.aliveBirths}`,
  livingChildren: `${AntenatalFormSections.parity}.${AntenatalFormFields.livingChildren}`,
  priorPregnancies: `${AntenatalFormSections.parity}.${AntenatalFormFields.priorPregnancies}`,
  iptpDoseOverride: `${AntenatalFormSections.ageOfPregnancy}.${AntenatalFormFields.iptpDoseOverride}`,
  vitals: `${AntenatalFormSections.investigations}.${AntenatalFormFields.vitals}`,
  syphillis: `${AntenatalFormSections.investigations}.${AntenatalFormFields.syphillis}`,
  hepatitisB: `${AntenatalFormSections.investigations}.${AntenatalFormFields.hepatitisB}`,
  hepatitisC: `${AntenatalFormSections.investigations}.${AntenatalFormFields.hepatitisC}`,
  haematology: `${AntenatalFormSections.investigations}.${AntenatalFormFields.haematology}`,
  bloodSugar: `${AntenatalFormSections.investigations}.${AntenatalFormFields.bloodSugar}`,
  urinalysis: `${AntenatalFormSections.investigations}.${AntenatalFormFields.urinalysis}`,
  associatedProblems: `${AntenatalFormSections.investigations}.${AntenatalFormFields.associatedProblems}`,
  hiv: `${AntenatalFormSections.counselling}.${AntenatalFormFields.hiv}`,
  maternalNutrition: `${AntenatalFormSections.counselling}.${AntenatalFormFields.maternalNutrition}`,
  breastfeeding: `${AntenatalFormSections.counselling}.${AntenatalFormFields.breastfeeding}`,
  exclusiveBreastfeeding: `${AntenatalFormSections.counselling}.${AntenatalFormFields.exclusiveBreastfeeding}`,
  femaleGenitalMutilation: `${AntenatalFormSections.counselling}.${AntenatalFormFields.femaleGenitalMutilation}`,
  longLastingInsecticidalNet: `${AntenatalFormSections.counselling}.${AntenatalFormFields.longLastingInsecticidalNet}`,
};

interface CardField {
  section: string;
  label?: MessageDescriptor;
  format: (v: unknown, encounter: Partial<AncEncounter>) => string;
}

export const fieldFormatters: Partial<Record<keyof AncEncounter, CardField>> = {
  earlyInitiationOfBreastfeeding: {
    section: "Counselling",
    label: defineMessage({
      defaultMessage: "Early initiation of breastfeeding",
      id: "anc.card.fields.breastfeeding",
    }),
    format: (value) => (value ? "Yes" : "No"),
  },
  exclusiveBreastfeeding: {
    section: "Counselling",
    label: defineMessage({
      defaultMessage: "Exclusive breastfeeding",
      id: "anc.card.fields.excludeBreastfeeding",
    }),
    format: (value) => (value ? "Yes" : "No"),
  },
  syphilisInvestigation: {
    section: "Investigations",
    label: defineMessage({
      defaultMessage: "Syphillis",
      id: "anc.card.fields.syphillisInvestigation",
    }),
    format: (value: AncInvestigationOutcome) =>
      toTitleCase(value.toString() || "unknown"),
  },
  hepatitisBInvestigation: {
    section: "Counselling",
    label: defineMessage({
      defaultMessage: "Hepatitis B investigation",
      id: "anc.card.fields.hepatitisBInvestigation",
    }),
    format: (value: AncInvestigationOutcome) =>
      toTitleCase(value.toString() || "unknown"),
  },
  femaleGenitalMutilation: {
    section: "Counselling",
    label: defineMessage({
      defaultMessage: "Female genital mutilation",
      id: "anc.card.fields.femaleGenitalMutilation",
    }),
    format: (value) => (value ? "Yes" : "No"),
  },
  hivTesting: {
    section: "Counselling",
    label: defineMessage({
      defaultMessage: "HIV testing",
      id: "anc.card.fields.hivTesting",
    }),
    format: (value: AncInvestigationOutcome) =>
      toTitleCase(value.toString() || "unknown"),
  },
  maternalNutrition: {
    section: "Counselling",
    label: defineMessage({
      defaultMessage: "Maternal nutrition",
      id: "anc.card.fields.maternalNutrition",
    }),
    format: (value) => (value ? "Yes" : "No"),
  },
  longLastingInsecticidalNet: {
    section: "Counselling",
    label: defineMessage({
      defaultMessage: "LLIN provided",
      id: "anc.card.fields.longLastingInsecticidalNet",
    }),
    format: (value) => (value ? "Yes" : "No"),
  },
  ageOfPregnancy: {
    label: defineMessage({
      defaultMessage: "Age of pregnancy",
      id: "anc.card.fields.ageOfPregnancy",
    }),
    section: "Parity",
    format: (value, encounter) => {
      const lmp = encounter?.lastMenstrualPeriod
        ? new Date(encounter.lastMenstrualPeriod)
        : null;
      const edd = encounter?.dueDate ? new Date(encounter.dueDate) : null;
      const visitDate = encounter?.createdAt
        ? new Date(encounter.createdAt)
        : null;
      if (lmp && visitDate) {
        const ga =
          edd && isEddManuallyOverridden(lmp, edd)
            ? computeGestationalAgeFromEdd(visitDate, edd)
            : computeGestationalAge(visitDate, lmp);
        if (ga) return `${ga.weeks} weeks + ${ga.days} days`;
      }
      return value != null ? `${value} weeks` : "Incomplete";
    },
  },
  dueDate: {
    label: defineMessage({
      defaultMessage: "Due date",
      id: "anc.card.fields.dueDate",
    }),
    section: "Parity",
    format: (value: Date, encounter) => {
      if (!value) return "Unknown";
      const lmp = encounter?.lastMenstrualPeriod
        ? new Date(encounter.lastMenstrualPeriod)
        : null;
      const formatted = new Date(value).toLocaleDateString();
      return lmp && isEddManuallyOverridden(lmp, new Date(value))
        ? `${formatted} (adjusted)`
        : formatted;
    },
  },
};
